import React, { useState } from 'react';
import { cn } from '../../lib/utils';
import CustomTooltip from './CustomTooltip';

interface HighlightedTextProps {
  justification?: Record<string, string[]> | string;
  segments?: Array<{
    text: string;
    isHighlighted: boolean;
    explanations?: string[];
  }>;
  className?: string;
}



interface HighlightProps {
  children: React.ReactNode;
  className?: string;
}

const Highlight: React.FC<HighlightProps> = ({ children, className }) => {
  return (
    <span
      className={cn(
        "relative inline rounded-sm",
        "bg-gradient-to-r from-blue-100 to-purple-100",
        "dark:from-blue-500/20 dark:to-purple-500/20",
        "text-foreground",
        className
      )}
    >
      {children}
    </span>
  );
};

const HighlightedText: React.FC<HighlightedTextProps> = ({ justification, segments: propSegments, className }) => {
  const [openTooltipIndex, setOpenTooltipIndex] = useState<number | null>(null);

  // Parse the justification object to extract phrases and their evidence (legacy format)
  const parseJustification = () => {
    if (!justification || typeof justification !== 'object') {
      return [];
    }

    const phrases = Object.keys(justification);
    // Join phrases with a single space - text is already cleaned by backend
    const fullText = phrases.join(' ');
    
    // Create segments with phrase information
    const segments: Array<{ text: string; evidence?: string[]; isHighlighted: boolean }> = [];
    let currentIndex = 0;
    
    phrases.forEach((phrase) => {
      // Text is already cleaned by backend, use as-is
      const phraseIndex = fullText.indexOf(phrase, currentIndex);
      
      // Add any text before this phrase
      if (phraseIndex > currentIndex) {
        segments.push({
          text: fullText.substring(currentIndex, phraseIndex),
          isHighlighted: false
        });
      }
      
      // Add the highlighted phrase
      const evidence = justification[phrase];
      if (evidence && evidence.length > 0) {
        segments.push({
          text: phrase,
          evidence,
          isHighlighted: true
        });
      } else {
        segments.push({
          text: phrase,
          isHighlighted: false
        });
      }
      
      currentIndex = phraseIndex + phrase.length;
    });
    
    // Add any remaining text
    if (currentIndex < fullText.length) {
      segments.push({
        text: fullText.substring(currentIndex),
        isHighlighted: false
      });
    }
    
    return segments;
  };

  // Smart highlighting: use justification text with segments for highlighting
  const createSmartSegments = () => {
    // If we have segments, use them directly
    if (propSegments && propSegments.length > 0) {
      return propSegments;
    }
    
    // If we have justification as string but no segments, create a simple segment
    if (typeof justification === 'string') {
      return [{
        text: justification,
        isHighlighted: false,
        explanations: []
      }];
    }
    
    // Fall back to legacy justification parsing
    return parseJustification();
  };

  // Use smart segments if both justification and segments are available, otherwise fallback
  const segments = createSmartSegments();

  return (
    <span className={cn("text-[var(--font-size-sm)]", className)}>
      {segments.map((segment, index) => {
        // Text is already cleaned by the backend, use as-is
        const segmentText = segment.text;

        // Add a space before the segment if:
        // 1. It's not the first segment (index > 0)
        // 2. The segment doesn't already start with a space
        // 3. The previous segment didn't end with a space or punctuation
        const needsLeadingSpace = index > 0 &&
          !segmentText.match(/^[\s.,!?;:]/) &&
          !segments[index - 1].text.match(/[\s.,!?;:]$/);

        const textWithSpace = needsLeadingSpace ? ' ' + segmentText : segmentText;

        if (segment.isHighlighted) {
          // Handle both new format (explanations) and legacy format (evidence)
          const tooltipContent = ('explanations' in segment && segment.explanations) ||
                                ('evidence' in segment && segment.evidence) ||
                                [];

          if (tooltipContent.length > 0) {
            return (
              <CustomTooltip
                key={index}
                content={
                  <ul className="list-disc pl-4 space-y-1">
                    {tooltipContent.map((explanation, i) => (
                      <li key={i}>{explanation}</li>
                    ))}
                  </ul>
                }
                placement="top"
                showArrow={true}
                delay={200}
                closeDelay={100}
                isOpen={openTooltipIndex === index}
                onOpenChange={(isOpen) => {
                  setOpenTooltipIndex(isOpen ? index : null);
                }}
              >
                <Highlight className="cursor-help">
                  {textWithSpace}
                </Highlight>
              </CustomTooltip>
            );
          }
        }
        return (
          <span key={index}>{textWithSpace}</span>
        );
      })}
    </span>
  );
};

export default HighlightedText;